/**
 * MockProvider — the whole app, offline.
 *
 * Rules it keeps:
 *  · deterministic IDs and tokens (no Math.random) so a demo is repeatable
 *  · integer rial arithmetic everywhere
 *  · realistic latency so loading states are exercised
 *  · dev scenario switches for network/payment/menu failures
 *  · every write is in-memory only and labelled demo — nothing here is a real transaction
 */
import { advanceMockDemoOrder } from './demoOrderTimeline';
import type {
  AdminMe,
  AdminContactLeadOut,
  AdminOrderList,
  AIChatInput,
  AIResponse,
  BranchOut,
  ContactLeadCreate,
  ContactLeadOut,
  DashboardSummary,
  MenuItemOut,
  MenuOut,
  RecommendationsOut,
  MockConfirmInput,
  OrderCreate,
  OrderItemOut,
  OrderOut,
  OrderPatch,
  PaymentAttemptOut,
  PaymentResult,
  PaymentShareDetailOut,
  RestaurantOut,
  RestaurantTableOut,
  ReviewCreate,
  ReviewOut,
  SessionCreate,
  SplitPaymentCreate,
  SplitPaymentOut,
  TableSessionOut,
} from '@/types/api';
import { ApiError } from '../api/http';
import { config } from '../config';
import { splitEquallyRounded, toPersianDigits, validateCustomShares } from '../formatting/money';
import type { CallOptions, DataProvider } from './provider';
import { MOCK_ITEMS, MOCK_ITEMS_BY_ID, MOCK_MENU, MOCK_RESTAURANT, mockReviewsForItem } from '@/mocks/menu';
import { answerFor } from '@/mocks/assistant';
import { buildMockRecommendations, type RecommendationParams } from '../recommendations';
import { scenarios } from '@/mocks/scenarios';
import {
  mutateMockState,
  nextSequence,
  readMockState,
  resetMockState,
  type StoredOrder,
  type StoredSplit,
} from './mockStore';

const SERVICE_FEE_PERCENT = 10;

/**
 * Deterministic counter-based IDs, persisted in the shared store so two tabs
 * can never mint the same token.
 */
const nextSeq = () => nextSequence();

function token(prefix: string, seed: number): string {
  // 12+ chars of [A-Za-z0-9_-], matching the backend's token pattern.
  return `${prefix}_${seed.toString(36).padStart(6, '0')}${(seed * 7919).toString(36).slice(-6)}`;
}

async function delay(ms: number, signal?: AbortSignal): Promise<void> {
  const scaled = scenarios.isOn('slowNetwork') ? ms * 4 : ms;
  await new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError('aborted', 0, 'درخواست لغو شد.'));
      return;
    }
    const timer = window.setTimeout(resolve, scaled);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new ApiError('aborted', 0, 'درخواست لغو شد.'));
      },
      { once: true },
    );
  });
}

/** Applies the dev "network failure" switch. */
function guardNetwork(): void {
  if (scenarios.isOn('networkFailure')) {
    throw new ApiError('network', 0, 'اتصال به سرور برقرار نشد. اینترنت را بررسی کنید و دوباره تلاش کنید.');
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

function dishNamed(name: string) {
  const dish = MOCK_ITEMS.find((item) => item.name_fa === name);
  if (!dish) throw new Error(`Mock fixture dish missing from the official menu: ${name}`);
  return dish;
}

export class MockProvider implements DataProvider {
  readonly mode = 'mock' as const;
  readonly isDemo = true;
  readonly paymentIsSimulated = true;

  /**
   * Session and admin state stay per-tab: they model a browser session, not
   * restaurant data. Everything the share link needs — orders, splits, shares,
   * payments, reviews — lives in the cross-tab store instead.
   */
  private sessions = new Map<string, TableSessionOut>();
  private leads: ContactLeadOut[] = [];
  private adminSession: AdminMe | null = null;

  constructor() {
    this.seedDemoOrders();
  }

  /** Clears shared demo state and re-seeds the board. For tests and the dev panel. */
  static resetSharedState(): void {
    resetMockState();
  }

  private lookupOrder(key: string): StoredOrder | undefined {
    return readMockState().orders[key];
  }

  /** Persists an order under every alias it answers to. */
  private saveOrder(order: StoredOrder): void {
    mutateMockState((state) => {
      state.orders[order.public_token] = order;
      state.orders[order.order_token] = order;
      state.orders[String(order.order_id)] = order;
    });
  }

  // ------------------------------------------------------------------ menu

  async getRestaurant(_restaurantId: string, o?: CallOptions): Promise<RestaurantOut> {
    guardNetwork();
    await delay(180, o?.signal);
    return MOCK_RESTAURANT;
  }

  async getRestaurantMenu(_restaurantId: string, o?: CallOptions): Promise<MenuOut> {
    guardNetwork();
    await delay(420, o?.signal);
    if (scenarios.isOn('emptyMenu')) {
      return { restaurant: MOCK_RESTAURANT, categories: [] };
    }
    if (scenarios.isOn('itemUnavailable')) {
      return {
        restaurant: MOCK_RESTAURANT,
        categories: MOCK_MENU.categories.map((category) => ({
          ...category,
          items: category.items.map((item) =>
            item.featured ? { ...item, available: false } : item,
          ),
        })),
      };
    }
    return MOCK_MENU;
  }

  async getRecommendations(
    _restaurantId: string,
    params: RecommendationParams = {},
    o?: CallOptions,
  ): Promise<RecommendationsOut> {
    guardNetwork();
    await delay(260, o?.signal);
    const menu = await this.getRestaurantMenu(_restaurantId, o);
    return buildMockRecommendations(menu, params);
  }

  async getBranches(_restaurantId: string, o?: CallOptions): Promise<BranchOut[]> {
    guardNetwork();
    await delay(150, o?.signal);
    return [
      {
        id: 1,
        restaurant_id: MOCK_RESTAURANT.id,
        code: 'fereshteh',
        name_fa: 'شعبهٔ فرشته',
        address_fa: 'تهران، خیابان فرشته، پلاک ۱۲',
        phone: '02122000000',
        active: true,
      },
    ];
  }

  async getBranchTables(_branchId: string, o?: CallOptions): Promise<RestaurantTableOut[]> {
    guardNetwork();
    await delay(150, o?.signal);
    // Persian labels on purpose: this is what a real restaurant prints, and it
    // is what the URL resolver has to cope with.
    return Array.from({ length: 14 }, (_unused, index) => ({
      id: index + 1,
      branch_id: 1,
      label: `میز ${toPersianDigits(index + 1)}`,
      qr_url: null,
      active: true,
    }));
  }

  async getMenuItem(itemId: string, o?: CallOptions): Promise<MenuItemOut> {
    guardNetwork();
    await delay(200, o?.signal);
    const item = MOCK_ITEMS_BY_ID[Number(itemId)];
    if (!item) throw new ApiError('not_found', 404, 'این آیتم در منو پیدا نشد.');
    return item;
  }

  async getReviews(_slug: string, limit = 20, o?: CallOptions): Promise<ReviewOut[]> {
    guardNetwork();
    await delay(220, o?.signal);
    const seeded = MOCK_ITEMS.flatMap((item) => mockReviewsForItem(item.id));
    return [...readMockState().reviews, ...seeded].slice(0, limit);
  }

  // --------------------------------------------------------------- session

  async createSession(input: SessionCreate, o?: CallOptions): Promise<TableSessionOut> {
    guardNetwork();
    await delay(260, o?.signal);
    const seed = nextSeq();
    // Mirrors the backend: a resolved table_id wins, else the raw label.
    const label =
      input.table_id !== undefined && input.table_id !== null
        ? String(input.table_id)
        : (input.table_label ?? config.defaultTableLabel);
    const session: TableSessionOut = {
      public_token: token('sess', seed),
      restaurant_id: MOCK_RESTAURANT.id,
      table_label: label,
      created_at: nowIso(),
      expires_at: new Date(Date.now() + 4 * 3600_000).toISOString(),
      session_id: String(seed),
      access_tier: 'open',
    };
    this.sessions.set(session.session_id, session);
    this.sessions.set(session.public_token, session);
    return session;
  }

  async getSession(sessionId: string, o?: CallOptions): Promise<TableSessionOut> {
    guardNetwork();
    await delay(160, o?.signal);
    const session = this.sessions.get(sessionId);
    if (!session) throw new ApiError('not_found', 404, 'این نشست میز پیدا نشد یا منقضی شده است.');
    return session;
  }

  // ---------------------------------------------------------------- orders

  private priceLines(items: OrderCreate['items']): OrderItemOut[] {
    return items.map((line, index) => {
      const item = MOCK_ITEMS_BY_ID[line.menu_item_id];
      if (!item) throw new ApiError('validation', 422, 'یکی از آیتم‌های سفارش در منو نیست.');
      if (!item.available) {
        throw new ApiError('conflict', 409, `«${item.name_fa}» دیگر موجود نیست. آن را از سبد بردارید.`);
      }
      const quantity = Math.max(1, Math.trunc(line.quantity));
      return {
        id: index + 1,
        menu_item_id: item.id,
        item_name_snapshot: item.name_fa,
        unit_price_irr: item.price_irr,
        quantity,
        selected_options: line.selected_options ?? {},
        line_total_irr: item.price_irr * quantity,
      };
    });
  }

  /** Server-side money. Integer arithmetic only. */
  private totals(lines: OrderItemOut[]) {
    const subtotal = lines.reduce((sum, line) => sum + line.line_total_irr, 0);
    const serviceFee = Math.trunc((subtotal * SERVICE_FEE_PERCENT) / 100);
    return { subtotal, serviceFee, grandTotal: subtotal + serviceFee };
  }

  async createOrder(input: OrderCreate, o?: CallOptions): Promise<OrderOut> {
    guardNetwork();
    await delay(560, o?.signal);
    if (input.items.length === 0) {
      throw new ApiError('validation', 422, 'سبد خالی است.');
    }
    const lines = this.priceLines(input.items);
    const money = this.totals(lines);
    const seed = nextSeq();
    const order: StoredOrder = {
      id: seed,
      order_id: seed,
      order_token: token('ord', seed),
      public_token: token('pub', seed),
      status: 'pending',
      payment_status: 'unpaid',
      customer_name: input.customer_name ?? null,
      customer_phone: input.customer_phone ?? null,
      notes: input.notes ?? null,
      subtotal_irr: money.subtotal,
      service_fee_irr: money.serviceFee,
      discount_irr: 0,
      grand_total_irr: money.grandTotal,
      revision: 1,
      created_at: nowIso(),
      updated_at: nowIso(),
      items: lines,
      _sessionToken: input.table_session_token,
    };
    this.saveOrder(order);
    return this.strip(order);
  }

  async getOrder(orderId: string, o?: CallOptions): Promise<OrderOut> {
    guardNetwork();
    await delay(180, o?.signal);
    const order = this.lookupOrder(orderId);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    if (advanceMockDemoOrder(order)) this.saveOrder(order);
    return this.strip(order);
  }

  async updateOrder(orderId: string, input: OrderPatch, o?: CallOptions): Promise<OrderOut> {
    guardNetwork();
    await delay(420, o?.signal);
    const order = this.lookupOrder(orderId);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    if (input.expected_revision !== order.revision) {
      throw new ApiError(
        'conflict',
        409,
        'این سفارش روی سرور تغییر کرده است. نسخهٔ تازه را بازخوانی کنید.',
        { current_revision: order.revision },
      );
    }
    if (order.status !== 'pending') {
      throw new ApiError('conflict', 409, 'آشپزخانه سفارش را تأیید کرده و ویرایش مستقیم ممکن نیست.');
    }
    if (input.items && input.items.length > 0) {
      order.items = this.priceLines(input.items);
      const money = this.totals(order.items);
      order.subtotal_irr = money.subtotal;
      order.service_fee_irr = money.serviceFee;
      order.grand_total_irr = money.grandTotal;
    }
    if (input.customer_name !== undefined) order.customer_name = input.customer_name ?? null;
    if (input.customer_phone !== undefined) order.customer_phone = input.customer_phone ?? null;
    if (input.notes !== undefined) order.notes = input.notes ?? null;
    order.revision += 1;
    order.updated_at = nowIso();
    this.saveOrder(order);
    return this.strip(order);
  }

  async confirmOrder(orderId: string, o?: CallOptions): Promise<OrderOut> {
    guardNetwork();
    await delay(360, o?.signal);
    const order = this.lookupOrder(orderId);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    if (order.payment_status !== 'paid') {
      throw new ApiError('conflict', 409, 'برای تأیید سفارش، ابتدا پرداخت را کامل کنید.');
    }
    if (order.status === 'confirmed') return this.strip(order);
    if (order.status !== 'pending') throw new ApiError('conflict', 409, 'این سفارش قبلاً تأیید شده است.');
    order.status = 'confirmed';
    order.demo_progress_started_at = nowIso();
    order.revision += 1;
    order.updated_at = nowIso();
    this.saveOrder(order);
    return this.strip(order);
  }

  async cancelOrder(orderId: string, o?: CallOptions): Promise<OrderOut> {
    guardNetwork();
    await delay(300, o?.signal);
    const order = this.lookupOrder(orderId);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    if (order.status !== 'pending') {
      throw new ApiError('conflict', 409, 'این سفارش دیگر قابل لغو نیست.');
    }
    order.status = 'cancelled';
    order.revision += 1;
    order.updated_at = nowIso();
    this.saveOrder(order);
    return this.strip(order);
  }

  private strip(order: StoredOrder): OrderOut {
    const { _sessionToken: _ignored, ...rest } = order;
    return { ...rest, items: [...rest.items] };
  }

  // -------------------------------------------------------------- payments

  async createPayment(orderPublicToken: string, o?: CallOptions): Promise<PaymentAttemptOut> {
    guardNetwork();
    await delay(340, o?.signal);
    const order = this.lookupOrder(orderPublicToken);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    const paid = this.paidForOrder(order.public_token);
    const amount = order.grand_total_irr - paid;
    if (amount <= 0) {
      throw new ApiError('conflict', 409, 'این صورت‌حساب قبلاً کامل پرداخت شده است.');
    }
    const seed = nextSeq();
    const paymentToken = token('pay', seed);
    mutateMockState((state) => {
      state.payments[paymentToken] = {
        orderToken: order.public_token,
        amountIrr: amount,
        status: 'pending',
      };
    });
    return {
      public_token: paymentToken,
      amount_irr: amount,
      status: 'pending',
      mock_redirect_url: `/pay/mock/${paymentToken}`,
      is_demo: true,
    };
  }

  async confirmMockPayment(
    paymentToken: string,
    input: MockConfirmInput,
    o?: CallOptions,
  ): Promise<PaymentResult> {
    guardNetwork();
    await delay(760, o?.signal);
    const payment = readMockState().payments[paymentToken];
    if (!payment) throw new ApiError('not_found', 404, 'این تراکنش پیدا نشد.');
    const forceFail = scenarios.isOn('paymentFailure');
    const success = forceFail ? false : input.success !== false;
    payment.status = success ? 'paid' : 'failed';
    mutateMockState((state) => {
      state.payments[paymentToken] = payment;
    });
    const order = this.lookupOrder(payment.orderToken);
    if (order && success) {
      order.payment_status =
        this.paidForOrder(order.public_token) >= order.grand_total_irr ? 'paid' : 'partially_paid';
      if (order.payment_status === 'paid' && order.status === 'pending') {
        order.status = 'confirmed';
        order.demo_progress_started_at = nowIso();
        order.revision += 1;
      }
      order.updated_at = nowIso();
      this.saveOrder(order);
    }
    return {
      payment_token: paymentToken,
      amount_irr: payment.amountIrr,
      payment_status: payment.status,
      order_payment_status: order?.payment_status ?? 'unpaid',
      is_demo: true,
      message_fa: success
        ? 'پرداخت با موفقیت ثبت شد.'
        : 'پرداخت ناموفق بود. سفارش شما پابرجاست.',
    };
  }

  private paidForOrder(orderPublicToken: string): number {
    const state = readMockState();
    let paid = 0;
    for (const payment of Object.values(state.payments)) {
      if (payment.orderToken === orderPublicToken && payment.status === 'paid') {
        paid += payment.amountIrr;
      }
    }
    for (const share of Object.values(state.shares)) {
      const split = state.splits[share.split_payment_token];
      if (split?.order_token === orderPublicToken && share.status === 'paid') {
        paid += share.amount_irr;
      }
    }
    return paid;
  }

  async createSplitPayment(
    orderPublicToken: string,
    input: SplitPaymentCreate,
    o?: CallOptions,
  ): Promise<SplitPaymentOut> {
    guardNetwork();
    await delay(520, o?.signal);
    const order = this.lookupOrder(orderPublicToken);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    const payable = order.grand_total_irr;

    let amounts: number[];
    let names: string[];

    if (input.mode === 'equal') {
      const people = Math.min(10, Math.max(2, Math.trunc(input.people_count ?? 2)));
      amounts = splitEquallyRounded(payable, people);
      names =
        input.names && input.names.length === people
          ? input.names
          : Array.from({ length: people }, (_unused, index) => `مهمان ${index + 1}`);
    } else {
      const shares = input.shares ?? [];
      if (shares.length < 2 || shares.length > 10) {
        throw new ApiError('validation', 422, 'تعداد نفرات باید بین ۲ تا ۱۰ نفر باشد.');
      }
      amounts = shares.map((share) => Math.trunc(share.amount_irr));
      names = shares.map((share) => share.display_name);
      const check = validateCustomShares(amounts, payable);
      if (!check.valid) throw new ApiError('validation', 422, check.messageFa);
    }

    const seed = nextSeq();
    const splitToken = token('spl', seed);
    const shareRecords: PaymentShareDetailOut[] = amounts.map((amount, index) => {
      const shareSeed = seed * 100 + index;
      const shareToken = token('shr', shareSeed);
      return {
        public_token: shareToken,
        order_token: order.public_token,
        split_payment_token: splitToken,
        display_name: names[index] ?? `مهمان ${index + 1}`,
        amount_irr: amount,
        status: 'pending',
        paid_at: null,
        mock_payment_url: `/pay/share/${shareToken}`,
        restaurant: MOCK_RESTAURANT,
        is_demo: true,
      };
    });
    // Persisted before the split so a share link opened immediately resolves.
    mutateMockState((state) => {
      shareRecords.forEach((share) => {
        state.shares[share.public_token] = share;
      });
    });

    const split: StoredSplit = {
      public_token: splitToken,
      order_token: order.public_token,
      mode: input.mode,
      payable_amount_irr: payable,
      paid_amount_irr: 0,
      shares: shareRecords.map((share) => ({
        public_token: share.public_token,
        display_name: share.display_name,
        amount_irr: share.amount_irr,
        status: share.status,
        paid_at: share.paid_at,
        mock_payment_url: share.mock_payment_url,
      })),
      is_demo: true,
      _expired: false,
    };
    mutateMockState((state) => {
      state.splits[splitToken] = split;
    });
    return this.readSplit(splitToken);
  }

  async getSplitPayment(publicToken: string, o?: CallOptions): Promise<SplitPaymentOut> {
    guardNetwork();
    await delay(200, o?.signal);
    return this.readSplit(publicToken);
  }

  private readSplit(publicToken: string): SplitPaymentOut {
    const state = readMockState();
    const split = state.splits[publicToken];
    if (!split) throw new ApiError('not_found', 404, 'این تقسیم صورت‌حساب پیدا نشد.');
    const shares = split.shares.map((share) => {
      const detail = state.shares[share.public_token];
      return detail
        ? {
            public_token: detail.public_token,
            display_name: detail.display_name,
            amount_irr: detail.amount_irr,
            status: detail.status,
            paid_at: detail.paid_at,
            mock_payment_url: detail.mock_payment_url,
          }
        : share;
    });
    const paid = shares
      .filter((share) => share.status === 'paid')
      .reduce((sum, share) => sum + share.amount_irr, 0);
    const { _expired: _ignored, ...rest } = split;
    return { ...rest, shares, paid_amount_irr: paid };
  }

  async getPaymentShare(shareToken: string, o?: CallOptions): Promise<PaymentShareDetailOut> {
    guardNetwork();
    await delay(240, o?.signal);
    if (scenarios.isOn('expiredShare')) {
      throw new ApiError('not_found', 404, 'این لینک پرداخت منقضی شده است. از میز لینک تازه بگیرید.');
    }
    const share = readMockState().shares[shareToken];
    if (!share) {
      throw new ApiError('not_found', 404, 'این لینک پرداخت معتبر نیست یا منقضی شده است.');
    }
    return share;
  }

  async confirmMockShare(
    shareToken: string,
    input: MockConfirmInput,
    o?: CallOptions,
  ): Promise<PaymentResult> {
    guardNetwork();
    await delay(700, o?.signal);
    const share = readMockState().shares[shareToken];
    if (!share) throw new ApiError('not_found', 404, 'این لینک پرداخت معتبر نیست.');
    if (share.status === 'paid') {
      throw new ApiError('conflict', 409, 'سهم شما قبلاً پرداخت شده است.');
    }
    const forceFail = scenarios.isOn('paymentFailure');
    const success = forceFail ? false : input.success !== false;
    share.status = success ? 'paid' : 'failed';
    share.paid_at = success ? nowIso() : null;
    // Persist the share first: paidForOrder reads it back to total the split.
    mutateMockState((state) => {
      state.shares[shareToken] = share;
    });

    const order = this.lookupOrder(share.order_token);
    if (order) {
      const paid = this.paidForOrder(order.public_token);
      order.payment_status =
        paid >= order.grand_total_irr ? 'paid' : paid > 0 ? 'partially_paid' : 'unpaid';
      if (order.payment_status === 'paid' && order.status === 'pending') {
        order.status = 'confirmed';
        order.demo_progress_started_at = nowIso();
        order.revision += 1;
      }
      order.updated_at = nowIso();
      this.saveOrder(order);
    }
    return {
      payment_token: shareToken,
      amount_irr: share.amount_irr,
      payment_status: share.status,
      order_payment_status: order?.payment_status ?? 'unpaid',
      is_demo: true,
      message_fa: success
        ? 'سهم شما با موفقیت پرداخت شد.'
        : 'پرداخت این سهم ناموفق بود. دوباره تلاش کنید.',
    };
  }

  // --------------------------------------------------------------- reviews

  async submitReview(input: ReviewCreate, o?: CallOptions): Promise<ReviewOut> {
    guardNetwork();
    await delay(480, o?.signal);
    const existing = readMockState().reviews.find(
      (review) => review.id === this.reviewIdFor(input.order_token),
    );
    if (existing) {
      throw new ApiError('conflict', 409, 'برای این سفارش قبلاً نظر ثبت کرده‌اید.');
    }
    const review: ReviewOut = {
      id: this.reviewIdFor(input.order_token),
      menu_item_id: input.menu_item_id ?? null,
      rating: Math.min(5, Math.max(1, Math.trunc(input.rating))),
      text: input.text,
      created_at: nowIso(),
      verified: true,
    };
    mutateMockState((state) => {
      state.reviews = [review, ...state.reviews];
    });
    return review;
  }

  private reviewIdFor(orderToken: string): number {
    let hash = 7;
    for (let i = 0; i < orderToken.length; i += 1) {
      hash = (hash * 31 + orderToken.charCodeAt(i)) % 100_000;
    }
    return 900_000 + hash;
  }

  // ------------------------------------------------------------- assistant

  async askTextAssistant(input: AIChatInput, o?: CallOptions): Promise<AIResponse> {
    guardNetwork();
    if (scenarios.isOn('assistantDown')) {
      await delay(300, o?.signal);
      throw new ApiError('server', 503, 'دستیار موقتاً در دسترس نیست. منو و سفارش‌گیری کار می‌کند.');
    }
    // Long enough that the typing indicator is real, short enough for a demo.
    await delay(640, o?.signal);
    const response = answerFor(input.message);
    return { ...response, conversation_token: input.conversation_token ?? 'conv_demo' };
  }

  async transcribeSpeech(_tableSessionToken: string, _audio: Blob, _durationMs: number, o?: CallOptions) {
    await delay(420, o?.signal);
    return {
      capability_available: true,
      transcript_fa: 'برای دو نفر چی پیشنهاد می‌دی؟',
      source: 'gemini' as const,
      message_fa: 'صدای نمونه به متن تبدیل شد.',
    };
  }

  // ------------------------------------------------------------- marketing

  async submitContactLead(_input: ContactLeadCreate, o?: CallOptions): Promise<ContactLeadOut> {
    guardNetwork();
    await delay(640, o?.signal);
    const lead: ContactLeadOut = {
      id: nextSeq(),
      created_at: nowIso(),
      status: 'received',
      // Says exactly what happened: nothing left the browser.
      message_fa: 'درخواست شما ثبت شد.',
    };
    this.leads = [lead, ...this.leads];
    return lead;
  }

  // ----------------------------------------------------------------- admin

  async adminLogin(email: string, password: string, o?: CallOptions): Promise<AdminMe> {
    guardNetwork();
    await delay(520, o?.signal);
    // Demo credentials, printed in the README and on the login screen itself.
    if (email.trim().toLowerCase() !== 'demo@garson.app' || password !== 'garson-demo') {
      throw new ApiError('unauthorized', 401, 'ایمیل یا گذرواژهٔ نمایشی درست نیست.');
    }
    this.adminSession = { email: email.trim().toLowerCase(), authenticated: true };
    return this.adminSession;
  }

  async adminLogout(o?: CallOptions): Promise<void> {
    await delay(160, o?.signal);
    this.adminSession = null;
  }

  async adminMe(o?: CallOptions): Promise<AdminMe> {
    await delay(140, o?.signal);
    if (!this.adminSession) throw new ApiError('unauthorized', 401, 'برای دیدن این صفحه وارد شوید.');
    return this.adminSession;
  }

  async adminOrders(
    params: { status?: string; limit?: number; offset?: number } = {},
    o?: CallOptions,
  ): Promise<AdminOrderList> {
    guardNetwork();
    await delay(380, o?.signal);
    const unique = new Map<string, StoredOrder>();
    for (const order of Object.values(readMockState().orders)) unique.set(order.public_token, order);
    let list = [...unique.values()].sort((a, b) => b.id - a.id);
    list.forEach((order) => { if (advanceMockDemoOrder(order)) this.saveOrder(order); });
    if (params.status) list = list.filter((order) => order.status === params.status);
    const offset = params.offset ?? 0;
    const limit = params.limit ?? 50;
    return {
      items: list.slice(offset, offset + limit).map((order) => this.strip(order)),
      total: list.length,
      is_demo_data: true,
    };
  }

  async adminSetOrderStatus(publicToken: string, status: string, o?: CallOptions): Promise<OrderOut> {
    guardNetwork();
    await delay(320, o?.signal);
    const order = this.lookupOrder(publicToken);
    if (!order) throw new ApiError('not_found', 404, 'این سفارش پیدا نشد.');
    order.status = status;
    order.revision += 1;
    order.updated_at = nowIso();
    this.saveOrder(order);
    return this.strip(order);
  }

  async adminDashboard(o?: CallOptions): Promise<DashboardSummary> {
    guardNetwork();
    await delay(420, o?.signal);
    const unique = new Map<string, StoredOrder>();
    for (const order of Object.values(readMockState().orders)) unique.set(order.public_token, order);
    const orders = [...unique.values()].filter((order) => order.status !== 'cancelled');
    const revenue = orders.reduce((sum, order) => sum + order.grand_total_irr, 0);
    const count = orders.length;
    const popular = [['آمریکن برگر', 18], ['پنه چیکن آلفردو', 14], ['سالاد سزار', 12], ['لاته مدیوم', 11]].map(([name, orders]) => {
      const dish = dishNamed(String(name));
      return { menu_item_id: dish.id, name_fa: dish.name_fa, order_count: Number(orders), revenue_irr: Number(orders) * dish.price_irr };
    });
    return {
      today_orders: count,
      today_revenue_irr: revenue,
      average_order_value_irr: count > 0 ? Math.trunc(revenue / count) : 0,
      popular_items: popular,
      rating_summary: { average: 4.7, count: 128 },
      is_demo_data: true,
    };
  }

  async adminContactLeads(o?: CallOptions): Promise<AdminContactLeadOut[]> {
    guardNetwork();
    await delay(150, o?.signal);
    return [];
  }

  async adminReviews(o?: CallOptions): Promise<ReviewOut[]> {
    guardNetwork();
    await delay(150, o?.signal);
    return [...readMockState().reviews];
  }

  /**
   * A few pre-existing orders so the admin board is never empty on first load.
   * Idempotent: the shared store is seeded once, not once per tab or reload.
   */
  private seedDemoOrders(): void {
    if (Object.keys(readMockState().orders).length > 0) return;
    const fixtures: Array<{ items: Array<[number, number]>; status: string; payment: string }> = [
      { items: [[dishNamed('آمریکن برگر').id, 2], [dishNamed('شیشه لیموناد').id, 2]], status: 'pending', payment: 'unpaid' },
      { items: [[dishNamed('پنه چیکن آلفردو').id, 1], [dishNamed('سالاد سزار').id, 1], [dishNamed('لاته مدیوم').id, 2]], status: 'confirmed', payment: 'unpaid' },
      { items: [[dishNamed('کته کوبه کباب').id, 1], [dishNamed('سیب زمینی سرخ شده').id, 1], [dishNamed('شیشه کوکا').id, 2]], status: 'preparing', payment: 'partially_paid' },
      { items: [[dishNamed('چیزبرگر').id, 1], [dishNamed('قوطی لیموناد').id, 1]], status: 'ready', payment: 'paid' },
      { items: [[dishNamed('سالاد سزار').id, 2], [dishNamed('شیک نوتلیتو').id, 2]], status: 'served', payment: 'paid' },
      { items: [[dishNamed('آیس لاته مدیوم').id, 1], [dishNamed('لاته مدیوم').id, 1]], status: 'served', payment: 'paid' },
    ];
    const names = ['سارا محمدی', 'امیر کاظمی', null, 'نگار رضایی', 'حسین طاهری', null];

    fixtures.forEach((fixture, index) => {
      const lines: OrderItemOut[] = fixture.items.map(([itemId, quantity], lineIndex) => {
        const item = MOCK_ITEMS_BY_ID[itemId];
        const price = item?.price_irr ?? 0;
        return {
          id: lineIndex + 1,
          menu_item_id: itemId,
          item_name_snapshot: item?.name_fa ?? 'آیتم حذف‌شده',
          unit_price_irr: price,
          quantity,
          selected_options: {},
          line_total_irr: price * quantity,
        };
      });
      const money = this.totals(lines);
      const seed = nextSeq();
      const order: StoredOrder = {
        id: seed,
        order_id: seed,
        order_token: token('ord', seed),
        public_token: token('pub', seed),
        status: fixture.status,
        payment_status: fixture.payment,
        customer_name: names[index] ?? null,
        customer_phone: null,
        notes: index === 0 ? 'سس تند جدا سرو شود' : null,
        subtotal_irr: money.subtotal,
        service_fee_irr: money.serviceFee,
        discount_irr: 0,
        grand_total_irr: money.grandTotal,
        revision: 1,
        created_at: new Date(Date.now() - (index + 1) * 11 * 60_000).toISOString(),
        updated_at: new Date(Date.now() - (index + 1) * 9 * 60_000).toISOString(),
        items: lines,
        _sessionToken: 'seed',
      };
      this.saveOrder(order);
    });
  }
}
